import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useLocation } from 'react-router-dom';

import { AuthScreen } from '@/features/auth/AuthScreen';
import { makeSigninState } from '@/features/auth/signinState';

/**
 * Gate for everything behind a login: an unauthenticated visitor is sent to
 * Keycloak, carrying the path they asked for so the callback can return them
 * to it. There is no anonymous mode — the whole dashboard sits behind this.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const location = useLocation();
  const returnTo = location.pathname + location.search;

  // Only redirect from a settled, signed-out state: while loading the session
  // may still turn out to be valid, and during an active navigation or after
  // an error another redirect would loop.
  const shouldRedirect =
    !auth.isAuthenticated && !auth.isLoading && !auth.activeNavigator && !auth.error;

  useEffect(() => {
    if (shouldRedirect) {
      void auth.signinRedirect({ state: makeSigninState(returnTo) });
    }
  }, [shouldRedirect, auth, returnTo]);

  if (auth.error) {
    return (
      <AuthScreen title={t('auth.signInFailed')}>
        <p>{auth.error.message}</p>
        <button
          type="button"
          onClick={() => void auth.signinRedirect({ state: makeSigninState(returnTo) })}
        >
          {t('common.tryAgain')}
        </button>
      </AuthScreen>
    );
  }

  if (auth.isAuthenticated) {
    return children;
  }

  return (
    <AuthScreen
      title={auth.isLoading ? t('auth.checkingSession') : t('auth.redirectingToSignIn')}
    />
  );
}
